import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Box,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  type ModalProps,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type Webcam from 'react-webcam';

import { IconTrashPurple, IconVolume } from '@/components';
import { VideoRecorder } from '@/components/VideoRecorder';
import { useGetVideoRecorderSourcesQuery } from '@/components/VideoRecorder/useVideoRecorderSource';
import { useFileAccessMutationLegacy } from '@/features/candidate/useCandidateTableMutation';
import { uploadObjectToS3 } from '@/utils/s3';
import { getTimerText } from '@/utils/timer';

import {
  adminInterviewKeys,
  useAddInterviewInstructionVideoMutation,
  useDeleteInterviewInstructionVideoMutation,
} from '../admin-interview/useAdminInterviewQueries';
import { useMediaDeviceContext } from '../media';
import { getSupportedMimeType } from '../video-recorder';
import { formatRecordingSize } from './common';

type RecordIntroductionVideoProps = Omit<ModalProps, 'children'> & {
  interviewId: string;
  interviewUrl?: string;
};

export const RecordIntroductionVideo: React.FC<RecordIntroductionVideoProps> = (
  props
) => {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const webcamRef = useRef<Webcam>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const analyserNodeRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);

  const [isUploadingVideo, setIsUploadingVideo] = useState<boolean>(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSize, setRecordingSize] = useState(0);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isStopped, setIsStopped] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [videoBlob, setVideoBlob] = useState<Blob | null>();
  const [volume, setVolume] = useState(1);

  const { t } = useTranslation(['interview']);
  const toast = useToast();
  const getPlayerSourcesQuery = useGetVideoRecorderSourcesQuery();

  const {
    selectedAudioDeviceId,
    setSelectedAudioDeviceId,
    selectedVideoDeviceId,
    setSelectedVideoDeviceId,
  } = useMediaDeviceContext();

  useEffect(() => {
    if (getPlayerSourcesQuery.data) {
      const audioDevice = getPlayerSourcesQuery.data.audioDevices[0];
      const videoDevice = getPlayerSourcesQuery.data.videoDevices[0];

      if (!audioDevice?.deviceId || !videoDevice?.deviceId) return;
      if (!!selectedVideoDeviceId || !!selectedAudioDeviceId) return;

      setSelectedAudioDeviceId(audioDevice.deviceId);
      setSelectedVideoDeviceId(videoDevice.deviceId);
    }
  }, [
    getPlayerSourcesQuery.data,
    selectedAudioDeviceId,
    selectedVideoDeviceId,
    setSelectedAudioDeviceId,
    setSelectedVideoDeviceId,
  ]);

  const handleStartCaptureClick = useCallback(() => {
    if (!webcamRef.current?.stream) return;
    const mimeType = getSupportedMimeType();

    mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream!, {
      mimeType: mimeType,
      videoBitsPerSecond: 500000,
    });

    const chunks: Array<BlobPart> = [];
    mediaRecorderRef.current.ondataavailable = (event) => {
      setRecordingSize((prevSize) => prevSize + event.data.size);
      chunks.push(event.data);
    };

    mediaRecorderRef.current.onstop = () => {
      const videoBlob = new Blob(chunks, { type: 'video/webm' });
      const url = URL.createObjectURL(videoBlob);
      setVideoUrl(url);
      setVideoBlob(videoBlob);
    };

    mediaRecorderRef.current.start(1000);

    audioContextRef.current = new AudioContext();
    const gainNode = audioContextRef.current.createGain();
    gainNode.gain.value = volume;
    gainNodeRef.current = gainNode;

    const analyserNode = audioContextRef.current.createAnalyser();
    analyserNode.fftSize = 256;
    analyserNodeRef.current = analyserNode;
    const dataArray = new Uint8Array(analyserNode.frequencyBinCount);
    dataArrayRef.current = dataArray;

    const audioTrack = webcamRef.current.stream!.getAudioTracks()[0];
    const source = audioContextRef.current.createMediaStreamSource(
      new MediaStream([audioTrack!])
    );
    source
      .connect(gainNode)
      .connect(analyserNode)
      .connect(audioContextRef.current.destination);

    const updateAudioLevel = () => {
      if (!analyserNodeRef.current || !dataArrayRef.current) return;
      analyserNodeRef.current.getByteFrequencyData(dataArrayRef.current);
      const avgVolume =
        dataArrayRef.current.reduce((a, b) => a + b, 0) /
        dataArrayRef.current.length;
      setAudioLevel(avgVolume);
      requestAnimationFrame(updateAudioLevel);
    };
    updateAudioLevel();
    setIsRecording(true);
    setRecordingTime(0);
    setRecordingSize(0);

    intervalRef.current = setInterval(() => {
      setRecordingTime((prevTime) => prevTime + 1);
    }, 1000);
  }, [webcamRef, mediaRecorderRef, volume]);

  const handleStopCaptureClick = useCallback(() => {
    if (!mediaRecorderRef?.current) return;

    if (intervalRef.current) clearInterval(intervalRef.current);
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
    setIsStopped(true);

    audioContextRef.current?.close();
    audioContextRef.current = null;
    gainNodeRef.current = null;
  }, [mediaRecorderRef]);

  const handleVolumeChange = (value: number) => {
    setVolume(value);
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = value;
    }
  };

  const handleRetry = () => {
    setIsStopped(false);
    setRecordingTime(0);
    setRecordingSize(0);
    setVideoBlob(null);
  };

  const queryClient = useQueryClient();
  const interviewInstructionVideoMutation =
    useAddInterviewInstructionVideoMutation({
      async onSuccess() {
        props.onClose();
        await queryClient.invalidateQueries({
          queryKey: adminInterviewKeys.getInterview({
            interviewsId: props.interviewId!,
          }),
        });
        toast({
          title: t('interview:introductionVideo.success'),
          status: 'success',
        });
      },
    });

  const deleteInterviewInstructionVideoMutation =
    useDeleteInterviewInstructionVideoMutation({
      async onSuccess() {
        props.onClose();
        await queryClient.invalidateQueries({
          queryKey: adminInterviewKeys.getInterview({
            interviewsId: props.interviewId!,
          }),
        });
        toast({
          title: t('interview:introductionVideo.deleteSuccessFull'),
          status: 'success',
        });
      },
    });

  const mediaAccessMutation = useFileAccessMutationLegacy();

  const submitAnswer = async (video: Blob) => {
    if (!props.interviewId) return;

    setIsUploadingVideo(true);
    const access = await mediaAccessMutation.mutateAsync({
      candidateId: 'candidateId',
      contentType: video.type,
      interviewId: props.interviewId,
      questionId: (
        new Date().getTime() * 10000 +
        621355968000000000
      ).toString(),
    });

    await uploadObjectToS3({
      blob: video,
      uploadUrl: access.signedUrl!,
      headers: {
        ...access.requestHeaders,
        'content-type': video.type,
      },
    });

    await interviewInstructionVideoMutation.mutateAsync({
      id: props.interviewId,
      body: {
        url: access.finalObjectUrl,
      },
    });

    setIsUploadingVideo(false);
  };

  const handleIntroductionVideoDelete = async () => {
    await deleteInterviewInstructionVideoMutation.mutateAsync({
      id: props.interviewId,
    });
  };

  return (
    <Modal {...props} isCentered>
      <ModalOverlay />
      <ModalContent
        bg="none"
        w="full"
        shadow="none"
        maxW="791px"
        mx="auto"
        justifyContent="center"
        alignItems="center"
      >
        <ModalBody
          position="relative"
          alignItems="center"
          w="791px"
          background="none"
          p={0}
        >
          <Stack zIndex={5} position="absolute" top="2" right="1">
            <ModalCloseButton
              borderWidth="1px"
              borderColor="gray.50"
              w={9}
              borderRadius="full"
              h={9}
              bg="white"
            />
          </Stack>

          {props.interviewUrl && (
            <RecordedVideoPlayer videoUrl={props.interviewUrl} />
          )}

          {!props.interviewUrl && (
            <VideoRecorderAndPreview
              webcamRef={webcamRef}
              isStopped={isStopped}
              videoUrl={videoUrl ?? ''}
              selectedAudioDeviceId={selectedAudioDeviceId}
              selectedVideoDeviceId={selectedVideoDeviceId}
            />
          )}

          {!isStopped && !props.interviewUrl && (
            <HStack
              position="absolute"
              bottom={5}
              width="full"
              alignItems="center"
              justifyContent="center"
            >
              <Stack
                position="relative"
                borderRadius="lg"
                h={20}
                w={350}
                bg="blackAlpha.600"
                p={3.5}
              >
                <HStack justifyContent="center">
                  <HStack position="absolute" top={4} left={3.5}>
                    <IconVolume color="white" />
                    <Slider
                      min={0}
                      max={1}
                      step={0.01}
                      value={volume}
                      onChange={handleVolumeChange}
                      width={12}
                      size="sm"
                      colorScheme="whiteAlpha"
                    >
                      <SliderTrack>
                        <SliderFilledTrack />
                      </SliderTrack>
                      <SliderThumb />
                    </Slider>
                  </HStack>
                  {isRecording ? (
                    <Box
                      onClick={handleStopCaptureClick}
                      _hover={{ cursor: 'pointer' }}
                      bg="white"
                      borderRadius="full"
                      w={7}
                      h={7}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Box w={4} h={4} borderRadius="md" bg="black" />
                    </Box>
                  ) : (
                    <Box
                      onClick={handleStartCaptureClick}
                      _hover={{ cursor: 'pointer' }}
                      borderRadius="full"
                      borderWidth={7}
                      p={0}
                      borderColor="error.100"
                      bg="error.500"
                      w={7}
                      h={7}
                    />
                  )}
                </HStack>
                <HStack justifyContent="space-between">
                  <Text w="14" textAlign="start" fontSize={12} color="white">
                    {getTimerText(recordingTime, '--:--')}
                  </Text>
                  <AudioLevelWithBar level={audioLevel} />
                  <Text w="14" textAlign="end" fontSize={12} color="white">
                    {formatRecordingSize(recordingSize)}
                  </Text>
                </HStack>
              </Stack>
            </HStack>
          )}
        </ModalBody>

        {isStopped && (
          <ModalFooter
            justifyContent="center"
            borderBottomRadius="lg"
            w="full"
            bg="white"
          >
            <HStack spacing={4}>
              <Button onClick={handleRetry} variant="outline">
                {t('interview:introductionVideo.tryAgain')}
              </Button>
              <Button
                isLoading={isUploadingVideo}
                onClick={() => videoBlob && submitAnswer(videoBlob)}
                w={200}
                variant="@primary"
              >
                {t('interview:introductionVideo.save')}
              </Button>
            </HStack>
          </ModalFooter>
        )}

        {props.interviewUrl && (
          <ModalFooter
            justifyContent="center"
            borderBottomRadius="lg"
            w="full"
            bg="white"
          >
            <HStack spacing={4}>
              <Button
                onClick={handleIntroductionVideoDelete}
                variant="outline"
                isLoading={deleteInterviewInstructionVideoMutation.isPending}
                w={200}
                color="primary.500!"
                leftIcon={<IconTrashPurple fontSize={20} />}
              >
                {t('interview:introductionVideo.deleteInterview')}
              </Button>
            </HStack>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};

type VideoRecorderAndPreviewProps = {
  isStopped: boolean;
  selectedAudioDeviceId: string | undefined;
  selectedVideoDeviceId: string | undefined;
  webcamRef: RefObject<Webcam>;
  videoUrl: string;
};

const VideoRecorderAndPreview: React.FC<VideoRecorderAndPreviewProps> = (
  props
) => {
  if (props.isStopped) return <RecordedVideoPlayer videoUrl={props.videoUrl} />;

  return (
    <VideoRecorder
      borderBottomRadius={props.isStopped ? 0 : 'lg'}
      ref={props.webcamRef}
      w="791px"
      mx="auto"
      h="526px"
      borderWidth="1px"
      borderColor="gray.50"
      audioConstraints={{
        deviceId: props.selectedAudioDeviceId,
      }}
      videoConstraints={{
        deviceId: props.selectedVideoDeviceId,
      }}
      maxH="60vh"
      ratio={16 / 9}
    />
  );
};

type AudioLevelWithBarProps = {
  level: number;
};
const AudioLevelWithBar = ({ level }: AudioLevelWithBarProps) => {
  const totalBars = 33;
  const centerIndex = Math.floor(totalBars / 2);
  const spreadFactor = 0.1;

  return (
    <HStack spacing={1}>
      {[...Array(totalBars)].map((_, index) => {
        const distanceFromCenter = Math.abs(index - centerIndex);
        const isWhite = level > 0 && distanceFromCenter <= level * spreadFactor;

        return (
          <Box key={_} w={0.5} h={3} bg={isWhite ? 'white' : '#D9D9D980'} />
        );
      })}
    </HStack>
  );
};

interface RecordedVideoPlayerProps {
  videoUrl: string | null;
}

const RecordedVideoPlayer: React.FC<RecordedVideoPlayerProps> = ({
  videoUrl,
}) => {
  if (!videoUrl) return null;

  return (
    <Flex
      pos="relative"
      overflow="hidden"
      bg="gray.700"
      roundedTop="xl"
      minH="350px"
      maxH="75dvh"
    >
      <video width="100%" src={videoUrl} controls />
    </Flex>
  );
};
